<template>
  <ContextColorProvider context="wealth">
    <HeroContainer>
      <template #navbar="{ isNavbarTransparent }">
        <NavBar :transparent="isNavbarTransparent">
          <template #left>
            <BackButton @click="goBack" />
          </template>
          <template #right>
            <ButtonIconContextual
              label="Copiar link"
              icon="EE0090"
              @click="handleShareButtonClick"
            />
          </template>
        </NavBar>
      </template>
      <template #hero>
        <Hero
          :image-small="headerImages.small"
          :image-medium="headerImages.regular"
          :image-large="headerImages.full"
          height="80vh"
          show-overlay
        >
          <TemplateHeader
            class="portfolio__header"
            :data="templateHeaderData"
            :is-loading="isLoading"
            :has-error="hasError"
            @on-retry="fetchPortfolioSheetData"
          />
        </Hero>
      </template>
      <template #content>
        <main class="portfolio__main" data-testid="main">
          <LoaderIndicator
            v-if="isLoading"
            class="portfolio__main__loader"
            size="36"
          />
          <HelperViewStatus
            v-else-if="hasError"
            class="portfolio__main__helper"
            title="Não foi possível carregar as informações dessa carteira"
            description="Aguarde alguns minutos e tente novamente."
            button-label="Tentar novamente"
            status="alert"
            @click="fetchPortfolioSheetData"
          />
          <div v-else>
            <TemplateStrategy
              v-if="portfolioSheetData?.strategy"
              class="portfolio__main__section"
              :data="portfolioSheetData.strategy"
            />
            <TemplateBenefits
              v-if="portfolioSheetData?.benefits"
              class="portfolio__main__section"
              :data="portfolioSheetData.benefits"
            />
            <Performance
              v-if="portfolioSheetData?.performance"
              class="portfolio__main__section"
              :data="portfolioSheetData.performance"
            />
            <Allocation
              v-if="portfolioSheetData?.allocation"
              class="portfolio__main__section"
              :allocation="portfolioSheetData.allocation"
            />
            <About
              v-if="portfolioSheetData?.about"
              class="portfolio__main__section"
              :about="portfolioSheetData.about"
            />
            <TermsModal
              :is-open="isTermsModalOpen"
              :data="portfolioSheetData?.terms"
              :template-id="templateId"
              @close="handleTermsModal"
              @accept="acceptTerms"
            />
          </div>
        </main>
        <ButtonContainer
          v-if="showButtonContainer"
          class="portfolio__button-container"
        >
          <template #button-primary>
            <ButtonPrimary
              :label="buttonLabel"
              size="large"
              :disabled="disableAcquisitionButton"
              @click="handleButtonClick"
            />
          </template>
        </ButtonContainer>
      </template>
    </HeroContainer>
    <ContinueRegistrationModal
      :is-open="isContinueRegistrationModalOpen"
      @close="isContinueRegistrationModalOpen = false"
    />
  </ContextColorProvider>
</template>

<script setup lang="ts">
import { computed, inject, ref, Ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  NavBar,
  ContextColorProvider,
  LoaderIndicator,
  HelperViewStatus,
  ButtonIconContextual,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { Hero, HeroContainer } from '@/modules/common/components/Hero';
import { TemplateHeader } from '@/modules/catalog/components/TemplateHeader';
import { TemplateStrategy } from '@/modules/catalog/components/TemplateStrategy';
import { TemplateBenefits } from '@/modules/catalog/components/TemplateBenefits';
import { About } from './components/About';
import { Performance } from './components/Performance';
import { Allocation } from './components/Allocation';
import { TermsModal } from './components/TermsModal';
import { ContinueRegistrationModal } from '@/modules/common/components/ContinueRegistrationModal';

import { IPortfolioSheetData } from '@/modules/catalog/services/variable-income';
import { EActionButton } from '@/modules/catalog/services/funds/funds-types';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { copyToClipboard } from '@/modules/common/helpers/browser';
import {
  isImpersonate,
  isImpersonatedByIntranet
} from '@/modules/common/services/partner';
import { CustomerModule } from '@/modules/common/store/CustomerModule';

const router = useRouter();

const fetchPortfolioSheetData = inject('fetchPortfolioSheetData', () => ({}));

const acceptTerms = inject('acceptTerms', (request: string) => ({ request }));

const portfolioSheetData = inject(
  'portfolioSheetData'
) as Ref<IPortfolioSheetData>;
const isLoading = inject('isLoading') as Ref<boolean>;
const hasError = inject('hasError') as Ref<boolean>;
const templateId = inject('templateId') as Ref<string>;

const isTermsModalOpen = ref(false);
const isContinueRegistrationModalOpen = ref(false);

const headerImages = computed(() => {
  return {
    small: portfolioSheetData.value?.header?.image?.small,
    regular: portfolioSheetData.value?.header?.image?.regular,
    full: portfolioSheetData.value?.header?.image?.full
  };
});

const templateHeaderData = computed(() => {
  return {
    title: portfolioSheetData.value?.header?.title ?? '-',
    description: portfolioSheetData.value?.header?.description ?? '-',
    isManagedByWarren: portfolioSheetData.value?.header?.isManagedByWarren
  };
});

const showButtonContainer = computed(() => {
  return (
    !isLoading.value &&
    !hasError.value &&
    portfolioSheetData.value?.terms
  );
});

const buttonLabel = computed(() => {
  return portfolioSheetData.value?.hasPortfolioCreated
    ? 'Você já possui essa carteira'
    : 'Investir';
});

const disableAcquisitionButton = computed(() => {
  return (
    portfolioSheetData.value?.hasPortfolioCreated ||
    isImpersonate() ||
    isImpersonatedByIntranet()
  );
});

const isRegulatoryStatusBlocked = computed(
  () => CustomerModule.isRegulatoryStatusBlocked
);

function handleShareButtonClick() {
  copyToClipboard(window.location.href);
  notify({
    group: 'wrn-notifications',
    text: 'Link copiado com sucesso.',
    type: NotificationTypes.success
  });
}

function handleButtonClick() {
  const actionButton = portfolioSheetData.value?.investment?.actionButton;
  
  if (actionButton?.action === EActionButton.ContinueRegistration) {
    isContinueRegistrationModalOpen.value = true;
    return;
  }

  handleTermsModal();
}

function handleTermsModal() {
  if (isRegulatoryStatusBlocked.value) {
    return router.push({ name: 'regulatoryUpdateAnnouncement' });
  }

  isTermsModalOpen.value = !isTermsModalOpen.value;
}

function goBack() {
  router.push({ name: 'catalog' });
}
</script>

<style lang="less" scoped>
.portfolio {
  &__header {
    max-width: 800px;
    margin: 0 auto @size-spacing-x900;
  }

  &__main {
    width: 100%;
    padding-top: @size-spacing-x1000;
    padding-bottom: 100px;

    &__loader {
      display: flex;
      justify-content: center;
    }

    &__helper {
      margin: 0 auto;
    }

    &__section {
      margin-bottom: @size-spacing-x1000;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
